import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import configService from '../services/configService';
import { useToast } from './Toast';

function getFlagEmoji(countryCode) {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

// Figma Design System
const designSystem = {
  colors: {
    primary: 'rgb(5, 94, 253)',
    background: 'rgb(255, 255, 255)',
    lightBackground: 'rgb(249, 249, 251)',
    text: 'rgb(34, 35, 36)',
    textSecondary: 'rgb(107, 114, 126)',
    border: 'rgb(229, 231, 235)',
    success: 'rgb(34, 197, 94)',
    error: 'rgb(239, 68, 68)',
    warning: 'rgb(245, 158, 11)'
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    fontWeights: { medium: 500, semibold: 600, bold: 700 }
  },
  borderRadius: '4px',
  spacing: { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' }
};

// Add CSS animation for spinner
const spinnerStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject CSS if not already present
if (!document.querySelector('#callRouting-spinner-style')) {
  const style = document.createElement('style');
  style.id = 'callRouting-spinner-style';
  style.textContent = spinnerStyle;
  document.head.appendChild(style);
}

// Design System Components
const PrimaryButton = ({ onClick, children, disabled = false, type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      backgroundColor: disabled ? 'rgb(156, 163, 175)' : designSystem.colors.primary,
      color: 'white',
      borderRadius: designSystem.borderRadius,
      padding: `${designSystem.spacing.md} ${designSystem.spacing.lg}`,
      fontFamily: designSystem.typography.fontFamily,
      fontSize: '13px',
      fontWeight: designSystem.typography.fontWeights.bold,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer'
    }}
  >
    {children}
  </button>
);

const SecondaryButton = ({ onClick, children, type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    style={{
      backgroundColor: designSystem.colors.background,
      color: designSystem.colors.textSecondary,
      border: `1px solid ${designSystem.colors.border}`,
      borderRadius: designSystem.borderRadius,
      padding: `${designSystem.spacing.md} ${designSystem.spacing.lg}`,
      fontFamily: designSystem.typography.fontFamily,
      fontSize: '13px',
      fontWeight: designSystem.typography.fontWeights.medium,
      cursor: 'pointer'
    }}
  >
    {children}
  </button>
);

const PhoneInput = ({ label, value, onChange, placeholder, required = false, countryCode, onCountryCodeChange }) => (
  <div style={{ marginBottom: designSystem.spacing.lg }}>
    <label style={{
      display: 'block',
      fontFamily: designSystem.typography.fontFamily,
      fontSize: '13px',
      fontWeight: designSystem.typography.fontWeights.medium,
      color: designSystem.colors.text,
      marginBottom: designSystem.spacing.xs
    }}>
      {label}
    </label>
    <div style={{ display: 'flex', gap: '8px' }}>
      <select 
        value={countryCode}
        onChange={onCountryCodeChange}
        style={{
          backgroundColor: designSystem.colors.background,
          border: `1px solid ${designSystem.colors.border}`,
          borderRadius: designSystem.borderRadius,
          padding: designSystem.spacing.md,
          fontFamily: designSystem.typography.fontFamily,
          fontSize: '13px',
          color: designSystem.colors.text,
          flexShrink: 0
        }}
      >
        <option value="+1">{getFlagEmoji('US')} +1</option>
        <option value="+1">{getFlagEmoji('CA')} +1</option>
        <option value="+44">{getFlagEmoji('GB')} +44</option>
        <option value="+33">{getFlagEmoji('FR')} +33</option>
        <option value="+49">{getFlagEmoji('DE')} +49</option>
        <option value="+39">{getFlagEmoji('IT')} +39</option>
        <option value="+34">{getFlagEmoji('ES')} +34</option>
        <option value="+61">{getFlagEmoji('AU')} +61</option>
        <option value="+81">{getFlagEmoji('JP')} +81</option>
      </select>
      <input
        type="tel"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        style={{
          flex: 1,
          backgroundColor: designSystem.colors.background,
          border: `1px solid ${designSystem.colors.border}`,
          borderRadius: designSystem.borderRadius,
          padding: designSystem.spacing.md,
          fontFamily: designSystem.typography.fontFamily,
          fontSize: '13px',
          color: designSystem.colors.text
        }}
      />
    </div>
  </div>
);

const Input = ({ label, type = 'text', value, onChange, placeholder, required = false, rows }) => (
  <div style={{ marginBottom: designSystem.spacing.lg }}>
    <label style={{
      display: 'block',
      fontFamily: designSystem.typography.fontFamily,
      fontSize: '13px',
      fontWeight: designSystem.typography.fontWeights.medium,
      color: designSystem.colors.text,
      marginBottom: designSystem.spacing.xs
    }}>
      {label}
    </label>
    {rows ? (
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        rows={rows}
        style={{
          width: '100%',
          backgroundColor: designSystem.colors.background,
          border: `1px solid ${designSystem.colors.border}`,
          borderRadius: designSystem.borderRadius,
          padding: designSystem.spacing.md,
          fontFamily: designSystem.typography.fontFamily,
          fontSize: '13px',
          color: designSystem.colors.text,
          resize: 'vertical'
        }}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        style={{
          width: '100%',
          backgroundColor: designSystem.colors.background,
          border: `1px solid ${designSystem.colors.border}`,
          borderRadius: designSystem.borderRadius,
          padding: designSystem.spacing.md,
          fontFamily: designSystem.typography.fontFamily,
          fontSize: '13px',
          color: designSystem.colors.text
        }}
      />
    )}
  </div>
);

const Select = ({ label, value, onChange, children, required = false }) => (
  <div style={{ marginBottom: designSystem.spacing.lg }}>
    <label style={{
      display: 'block',
      fontFamily: designSystem.typography.fontFamily,
      fontSize: '13px',
      fontWeight: designSystem.typography.fontWeights.medium,
      color: designSystem.colors.text,
      marginBottom: designSystem.spacing.xs
    }}>
      {label}
    </label>
    <select
      value={value}
      onChange={onChange}
      required={required}
      style={{
        width: '100%',
        backgroundColor: designSystem.colors.background,
        border: `1px solid ${designSystem.colors.border}`,
        borderRadius: designSystem.borderRadius,
        padding: designSystem.spacing.md,
        fontFamily: designSystem.typography.fontFamily,
        fontSize: '13px',
        color: designSystem.colors.text
      }}
    >
      {children}
    </select>
  </div>
);

const CallRoutingSettings = ({ cachedRules, adminBusinessId }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [rules, setRules] = useState(cachedRules || []);
  const [loading, setLoading] = useState(!cachedRules);
  const [showAddRule, setShowAddRule] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [setupIncomplete, setSetupIncomplete] = useState(false);

  const effectiveUserId = adminBusinessId || user?.attributes?.sub;

  useEffect(() => {
    if (cachedRules) {
      console.log('[CallRoutingSettings] Using cached rules:', cachedRules.length);
      setRules(cachedRules);
      setLoading(false);
    } else {
      loadRules();
    }
  }, [cachedRules]);

  const loadRules = async () => {
    try {
      const token = await configService.getAuthToken();
      const cognitoUserId = effectiveUserId;
      
      console.log('[CallRoutingSettings] Loading rules:', {
        cognitoUserId,
        isAdminMode: !!adminBusinessId
      });
      
      const response = await fetch(`https://oauth.remote-assistant.ai/oauth/users/${cognitoUserId}/routing-rules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[CallRoutingSettings] Loaded rules:', data.rules?.length || 0);
        setRules(data.rules || []);
      } else if (response.status === 404) {
        try {
          const errorData = await response.json();
          if (errorData.detail === 'Business config not found') {
            console.warn('Business config not found - user needs to complete setup first');
            setSetupIncomplete(true);
            setRules([]);
          } else {
            console.error('Failed to load routing rules:', response.status, errorData);
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          setSetupIncomplete(true);
          setRules([]);
        }
      } else {
        console.error('Failed to load routing rules:', response.status);
      }
    } catch (error) {
      console.error('Failed to load routing rules:', error);
      // If we get a JSON parse error with HTML content, it means the endpoint isn't properly configured
      if (error.message && error.message.includes('Unexpected token')) {
        console.warn('Routing rules endpoint returned HTML instead of JSON - feature not yet available');
        setSetupIncomplete(true);
      }
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  const addRule = async (ruleData) => {
    try {
      const token = await configService.getAuthToken();
      const cognitoUserId = configService.getUserId();
      const response = await fetch(`https://oauth.remote-assistant.ai/oauth/users/${cognitoUserId}/routing-rules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ruleData)
      });
      
      if (response.ok) {
        loadRules();
        setShowAddRule(false);
        showToast('Saved successfully!');
      } else {
        showToast('Failed to save', 'error');
      }
    } catch (error) {
      showToast('Failed to save', 'error');
    }
  };

  const updateRule = async (ruleId, ruleData) => {
    try {
      const token = await configService.getAuthToken();
      const cognitoUserId = configService.getUserId();
      const response = await fetch(`https://oauth.remote-assistant.ai/oauth/users/${cognitoUserId}/routing-rules/${ruleId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ruleData)
      });
      
      if (response.ok) {
        loadRules();
        setEditingRuleId(null);
        showToast('Saved successfully!');
      } else {
        showToast('Failed to save', 'error');
      }
    } catch (error) {
      showToast('Failed to save', 'error');
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      const token = await configService.getAuthToken();
      const cognitoUserId = configService.getUserId();
      await fetch(`https://oauth.remote-assistant.ai/oauth/users/${cognitoUserId}/routing-rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadRules();
    } catch (error) {
      console.error('Failed to delete routing rule:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: designSystem.spacing.xl }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: `4px solid ${designSystem.colors.border}`,
          borderTop: `4px solid ${designSystem.colors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: `0 auto ${designSystem.spacing.lg}`
        }}></div>
        <p style={{
          fontFamily: designSystem.typography.fontFamily,
          color: designSystem.colors.textSecondary,
          fontSize: '14px'
        }}>Loading routing settings...</p>
      </div>
    );
  }

  if (setupIncomplete) {
    return (
      <div style={{
        backgroundColor: 'rgb(239, 246, 255)',
        border: `1px solid rgb(147, 197, 253)`,
        borderRadius: designSystem.borderRadius,
        padding: designSystem.spacing.xl,
        textAlign: 'center'
      }}>
        <h3 style={{
          fontFamily: designSystem.typography.fontFamily,
          fontSize: '18px',
          fontWeight: designSystem.typography.fontWeights.semibold,
          color: 'rgb(30, 64, 175)',
          marginBottom: designSystem.spacing.sm
        }}>Call Routing Coming Soon</h3>
        <p style={{
          fontFamily: designSystem.typography.fontFamily,
          color: 'rgb(29, 78, 216)',
          marginBottom: designSystem.spacing.lg,
          fontSize: '14px'
        }}>
          Advanced call routing features are currently being developed and will be available soon.
        </p>
        <p style={{
          fontFamily: designSystem.typography.fontFamily,
          fontSize: '13px',
          color: 'rgb(37, 99, 235)'
        }}>
          In the meantime, your assistant will handle all calls directly.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: designSystem.spacing.xl,
        flexWrap: 'wrap',
        gap: designSystem.spacing.sm
      }}>
        <h3 style={{
          fontFamily: designSystem.typography.fontFamily,
          fontSize: '18px',
          fontWeight: designSystem.typography.fontWeights.semibold,
          color: designSystem.colors.text,
          margin: 0
        }}>Call Routing Rules</h3>
        <PrimaryButton onClick={() => setShowAddRule(true)}>
          Add Rule
        </PrimaryButton>
      </div>

      {showAddRule && (
        <AddRuleForm 
          onSave={addRule} 
          onCancel={() => setShowAddRule(false)} 
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: designSystem.spacing.lg }}>
        {rules.map(rule => (
          <RuleCard 
            key={rule.rule_id} 
            rule={rule} 
            isEditing={editingRuleId === rule.rule_id}
            onEdit={() => setEditingRuleId(rule.rule_id)}
            onSave={(ruleData) => updateRule(rule.rule_id, ruleData)}
            onCancel={() => setEditingRuleId(null)}
            onDelete={() => deleteRule(rule.rule_id)}
          />
        ))}
        
        {rules.length === 0 && (
          <div style={{
            fontFamily: designSystem.typography.fontFamily,
            color: designSystem.colors.textSecondary,
            textAlign: 'center',
            padding: designSystem.spacing.xl,
            fontSize: '14px'
          }}>
            No routing rules configured. Add your first rule to get started.
          </div>
        )}
      </div>
    </div>
  );
};

const RuleCard = ({ rule, isEditing, onEdit, onSave, onCancel, onDelete }) => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isEditing) {
    return <EditRuleForm rule={rule} onSave={onSave} onCancel={onCancel} />;
  }

  return (
    <div style={{
      border: `1px solid ${designSystem.colors.border}`,
      borderRadius: designSystem.borderRadius,
      padding: designSystem.spacing.lg,
      backgroundColor: designSystem.colors.background
    }}>
      {/* Header with title and actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: designSystem.spacing.sm
      }}>
        <h4 style={{
          fontFamily: designSystem.typography.fontFamily,
          fontWeight: designSystem.typography.fontWeights.medium,
          color: designSystem.colors.text,
          fontSize: '16px',
          margin: 0,
          flex: 1
        }}>{rule.rule_name}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: designSystem.spacing.sm, marginLeft: designSystem.spacing.sm }}>
          <span style={{
            padding: `${designSystem.spacing.xs} ${designSystem.spacing.sm}`,
            fontSize: '11px',
            borderRadius: designSystem.borderRadius,
            fontFamily: designSystem.typography.fontFamily,
            fontWeight: designSystem.typography.fontWeights.medium,
            backgroundColor: rule.enabled ? 'rgb(220, 252, 231)' : designSystem.colors.lightBackground,
            color: rule.enabled ? 'rgb(22, 163, 74)' : designSystem.colors.textSecondary,
            whiteSpace: 'nowrap'
          }}>
            {rule.enabled ? 'Enabled' : 'Disabled'}
          </span>
          <button 
            onClick={onEdit}
            title="Edit rule"
            style={{
              color: designSystem.colors.primary,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: designSystem.spacing.xs
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button 
            onClick={onDelete}
            title="Delete rule"
            style={{
              color: designSystem.colors.error,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: designSystem.spacing.xs
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Content area - full width on mobile */}
      <div style={{
        fontFamily: designSystem.typography.fontFamily,
        fontSize: '13px',
        color: designSystem.colors.textSecondary
      }}>
        <div style={{ marginBottom: designSystem.spacing.xs }}>
          <span style={{ fontWeight: designSystem.typography.fontWeights.medium }}>Type:</span> {rule.criteria.type}
        </div>
        
        {/* Description gets full width */}
        <div style={{ marginBottom: designSystem.spacing.sm }}>
          <span style={{ fontWeight: designSystem.typography.fontWeights.medium }}>Description:</span>
          <div style={{
            backgroundColor: 'rgb(239, 246, 255)',
            padding: designSystem.spacing.sm,
            borderRadius: designSystem.borderRadius,
            marginTop: designSystem.spacing.xs,
            color: 'rgb(30, 64, 175)',
            fontSize: '12px',
            lineHeight: '1.4'
          }}>
            {rule.criteria.description}
          </div>
        </div>
        
        {/* Route info - stack on mobile */}
        <div style={{
          display: isMobile ? 'block' : 'flex',
          gap: isMobile ? designSystem.spacing.xs : designSystem.spacing.lg,
          flexWrap: 'wrap'
        }}>
          <div style={{ marginBottom: isMobile ? designSystem.spacing.xs : 0 }}>
            <span style={{ fontWeight: designSystem.typography.fontWeights.medium }}>Route to:</span> {rule.routing_action.destination_phone} ({rule.routing_action.destination_name})
          </div>
          <div>
            <span style={{ fontWeight: designSystem.typography.fontWeights.medium }}>Priority:</span> {rule.priority}
          </div>
        </div>
      </div>
    </div>
  );
};

const AddRuleForm = ({ onSave, onCancel }) => {
  const [countryCode, setCountryCode] = useState('+1');
  const [formData, setFormData] = useState({
    rule_name: '',
    criteria: {
      type: 'escalation',
      description: '',
      sentiment_threshold: 0.7
    },
    routing_action: {
      type: 'transfer',
      destination_phone: '',
      destination_name: '',
      fallback_action: 'sms_notify'
    },
    priority: 1,
    enabled: true
  });
  
  const getDescriptionTemplate = (type) => {
    const templates = {
      escalation: "Route the call when the customer is asking for something the assistant is not configured to handle, when specifically requesting to speak to a manager or supervisor, or when expressing frustration with service quality that cannot be resolved",
      department: "Route to this department when customer needs help with [specific services/issues], has questions about [topic area], or requests assistance that requires specialized knowledge in [domain]",
      callback: "Schedule a callback when customer requests to speak with someone but it is outside business hours, or when they need specialized help that requires human assistance during business hours"
    };
    return templates[type] || templates.escalation;
  };
  
  const handleRuleTypeChange = (newType) => {
    setFormData({
      ...formData,
      criteria: {
        ...formData.criteria,
        type: newType,
        description: getDescriptionTemplate(newType)
      }
    });
  };
  
  React.useEffect(() => {
    if (!formData.criteria.description) {
      setFormData(prev => ({
        ...prev,
        criteria: {
          ...prev.criteria,
          description: getDescriptionTemplate(prev.criteria.type)
        }
      }));
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const phoneDigits = formData.routing_action.destination_phone.replace(/\D/g, '');
    const fullPhone = phoneDigits ? `${countryCode}${phoneDigits}` : '';
    onSave({
      ...formData,
      routing_action: {
        ...formData.routing_action,
        destination_phone: fullPhone
      }
    });
  };

  return (
    <div style={{
      border: `1px solid ${designSystem.colors.border}`,
      borderRadius: designSystem.borderRadius,
      padding: designSystem.spacing.lg,
      backgroundColor: designSystem.colors.lightBackground,
      marginBottom: designSystem.spacing.lg
    }}>
      <h4 style={{
        fontFamily: designSystem.typography.fontFamily,
        fontSize: '16px',
        fontWeight: designSystem.typography.fontWeights.medium,
        color: designSystem.colors.text,
        marginBottom: designSystem.spacing.lg
      }}>Add New Routing Rule</h4>
      
      <form onSubmit={handleSubmit}>
        <Input
          label="Rule Name"
          type="text"
          value={formData.rule_name}
          onChange={(e) => setFormData({...formData, rule_name: e.target.value})}
          placeholder="e.g., Manager Escalation, Technical Support, After Hours"
          required
        />

        <Select
          label="Rule Type"
          value={formData.criteria.type}
          onChange={(e) => handleRuleTypeChange(e.target.value)}
          required
        >
          <option value="escalation">Manager Escalation</option>
          <option value="department">Department Transfer</option>
          <option value="callback">Schedule Callback</option>
        </Select>

        <Input
          label="Routing Criteria"
          value={formData.criteria.description}
          onChange={(e) => setFormData({
            ...formData,
            criteria: {...formData.criteria, description: e.target.value}
          })}
          rows={3}
          required
        />

        <PhoneInput
          label="Destination Phone"
          value={formData.routing_action.destination_phone}
          onChange={(e) => setFormData({
            ...formData,
            routing_action: {...formData.routing_action, destination_phone: e.target.value}
          })}
          placeholder="123-456-7890"
          required
          countryCode={countryCode}
          onCountryCodeChange={(e) => setCountryCode(e.target.value)}
        />

        <Input
          label="Destination Name"
          type="text"
          value={formData.routing_action.destination_name}
          onChange={(e) => setFormData({
            ...formData,
            routing_action: {...formData.routing_action, destination_name: e.target.value}
          })}
          placeholder="Manager, Technical Support, Billing Department"
          required
        />

        <Input
          label="Priority (1 = highest)"
          type="number"
          value={formData.priority}
          onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
          required
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: designSystem.spacing.sm }}>
          <SecondaryButton onClick={onCancel}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit">
            Create Rule
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
};

const EditRuleForm = ({ rule, onSave, onCancel }) => {
  const [countryCode, setCountryCode] = useState('+1');
  const [formData, setFormData] = useState(rule);
  
  React.useEffect(() => {
    if (rule.routing_action.destination_phone) {
      const allDigits = rule.routing_action.destination_phone.replace(/\D/g, '');
      const countryCodeMap = {
        '1': { code: '+1', length: 1 },
        '44': { code: '+44', length: 2 },
        '33': { code: '+33', length: 2 },
        '49': { code: '+49', length: 2 },
        '39': { code: '+39', length: 2 },
        '34': { code: '+34', length: 2 },
        '61': { code: '+61', length: 2 },
        '81': { code: '+81', length: 2 }
      };
      
      let extractedCountryCode = '+1';
      let displayPhone = '';
      
      for (const [digits, info] of Object.entries(countryCodeMap)) {
        if (allDigits.startsWith(digits)) {
          extractedCountryCode = info.code;
          displayPhone = allDigits.slice(info.length);
          break;
        }
      }
      
      if (!displayPhone && allDigits.length === 10) {
        displayPhone = allDigits;
      }
      
      setCountryCode(extractedCountryCode);
      setFormData({
        ...rule,
        routing_action: {
          ...rule.routing_action,
          destination_phone: displayPhone
        }
      });
    }
  }, [rule]);
  
  const getDescriptionTemplate = (type) => {
    const templates = {
      escalation: "Route the call when the customer is asking for something the assistant is not configured to handle, when specifically requesting to speak to a manager or supervisor, or when expressing frustration with service quality that cannot be resolved",
      department: "Route to this department when customer needs help with [specific services/issues], has questions about [topic area], or requests assistance that requires specialized knowledge in [domain]",
      callback: "Schedule a callback when customer requests to speak with someone but it is outside business hours, or when they need specialized help that requires human assistance during business hours"
    };
    return templates[type] || templates.escalation;
  };
  
  // Auto-fill template when rule type changes
  const handleRuleTypeChange = (newType) => {
    setFormData({
      ...formData,
      criteria: {
        ...formData.criteria,
        type: newType,
        description: getDescriptionTemplate(newType)
      }
    });
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const phoneDigits = formData.routing_action.destination_phone.replace(/\D/g, '');
    const fullPhone = phoneDigits ? `${countryCode}${phoneDigits}` : '';
    onSave({
      ...formData,
      routing_action: {
        ...formData.routing_action,
        destination_phone: fullPhone
      }
    });
  };

  return (
    <div style={{
      border: `1px solid ${designSystem.colors.border}`,
      borderRadius: designSystem.borderRadius,
      padding: designSystem.spacing.lg,
      backgroundColor: designSystem.colors.lightBackground
    }}>
      <h4 style={{
        fontFamily: designSystem.typography.fontFamily,
        fontSize: '16px',
        fontWeight: designSystem.typography.fontWeights.medium,
        color: designSystem.colors.text,
        marginBottom: designSystem.spacing.lg
      }}>Edit Routing Rule</h4>
        
        <form onSubmit={handleSubmit}>
          <Input
            label="Rule Name"
            type="text"
            value={formData.rule_name}
            onChange={(e) => setFormData({...formData, rule_name: e.target.value})}
            placeholder="e.g., Manager Escalation, Technical Support, After Hours"
            required
          />

          <Select
            label="Rule Type"
            value={formData.criteria.type}
            onChange={(e) => handleRuleTypeChange(e.target.value)}
            required
          >
            <option value="escalation">Manager Escalation</option>
            <option value="department">Department Transfer</option>
            <option value="callback">Schedule Callback</option>
          </Select>

          <Input
            label="Routing Criteria"
            value={formData.criteria.description}
            onChange={(e) => setFormData({
              ...formData,
              criteria: {...formData.criteria, description: e.target.value}
            })}
            rows={3}
            required
          />


          <PhoneInput
            label="Destination Phone"
            value={formData.routing_action.destination_phone}
            onChange={(e) => setFormData({
              ...formData,
              routing_action: {...formData.routing_action, destination_phone: e.target.value}
            })}
            placeholder="123-456-7890"
            required
            countryCode={countryCode}
            onCountryCodeChange={(e) => setCountryCode(e.target.value)}
          />

          <Input
            label="Destination Name"
            type="text"
            value={formData.routing_action.destination_name}
            onChange={(e) => setFormData({
              ...formData,
              routing_action: {...formData.routing_action, destination_name: e.target.value}
            })}
            placeholder="Manager, Technical Support, Billing Department"
            required
          />

          <Input
            label="Priority (1 = highest)"
            type="number"
            value={formData.priority}
            onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
            required
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: designSystem.spacing.sm }}>
            <SecondaryButton onClick={onCancel}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit">
              Update Rule
            </PrimaryButton>
          </div>
        </form>
    </div>
  );
};

export default CallRoutingSettings;